import { useState } from "react";
import { useStripeComponents } from "../../contexts/StripeProvider";
import { BasePaymentProviderReturn, PaymentProvider } from "./usePayment";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { CardElement } from "@stripe/react-stripe-js";
import { config } from "../../config";
import { getTranslation } from "../../../shared/modules/config";
import errorNotifier from "../../../shared/modules/error-notifier";
import {
  extractUserFriendlyErrorMessage,
  UserFacingError,
} from "../util/error";

interface StripePaymentProps {
  secret: string;
}

interface ConfirmCardPaymentParams {
  stripe: Stripe | null;
  elements: StripeElements | null;
  secret: string;
}

export interface StripePaymentResult {
  paymentId?: string;
}

export const confirmCardPayment = async ({
  stripe,
  elements,
  secret,
}: ConfirmCardPaymentParams): Promise<StripePaymentResult> => {
  if (!stripe || !elements) {
    throw new Error("Stripe or Elements not initialized");
  }
  const cardElement = elements.getElement(CardElement);
  if (!cardElement) {
    throw new Error("CardElement not found");
  }

  return stripe
    .confirmCardPayment(secret, {
      payment_method: {
        card: cardElement,
        billing_details: {},
      },
      setup_future_usage: "off_session",
    })
    .then((result) => {
      if (result.error) {
        throw new UserFacingError(result.error.message);
      } else {
        if (result.paymentIntent?.status === "requires_capture") {
          return { paymentId: result.paymentIntent.id };
        }
        throw new UserFacingError(
          "Payment was not captured correctly. Please contact support.",
        );
      }
    });
};

const useStripePayment = ({
  secret,
}: StripePaymentProps): BasePaymentProviderReturn => {
  const [capturingPayment, setCapturingPayment] = useState(false);
  const { stripe, elements: stripeElement } = useStripeComponents();
  const [paymentError, setPaymentError] = useState<string>();
  const [succeededPaymentIntentId, setSucceededPaymentIntentId] = useState("");
  const isPaymentConfigurationError = !!config?.isStripeEnabled && !secret;

  const makePayment = async (): Promise<StripePaymentResult> => {
    setCapturingPayment(true);
    setPaymentError("");
    setSucceededPaymentIntentId("");

    try {
      const result = await confirmCardPayment({
        stripe,
        elements: stripeElement,
        secret,
      });
      setSucceededPaymentIntentId(result?.paymentId ?? "");
      return result;
    } catch (error: unknown) {
      setPaymentError(
        extractUserFriendlyErrorMessage(
          error,
          getTranslation("shop_now_checkout_payment_error_default"),
        ),
      );
      errorNotifier.critical("Stripe payment error", {
        details: error,
      });
      throw error;
    } finally {
      setCapturingPayment(false);
    }
  };

  return {
    capturingPayment,
    succeededPaymentIntentId,
    makePayment,
    paymentError,
    isPaymentConfigurationError,
    eligiblePaymentProvider: PaymentProvider.STRIPE,
  };
};

export default useStripePayment;
